// The owner's dashboard by date (D-144): the date filter, the day's numbers, the agents side by side, everyone's calls
// through the day as a heat map, and one person's day half hour by half hour (D-145). Charts are plain HTML: thin bars, a
// 2px gap between stacked parts, hairline grids, a legend for two series, a tooltip on hover and focus, and a table view
// of every chart.
import { type ReactNode, useRef, useState } from "react";
import {
  agentsToShow,
  cellOf,
  connectRate,
  fmtTalk,
  halfColumns,
  halfSpan,
  HEAT_STEPS,
  heatStep,
  hourColumns,
  hourLabel,
  hourSpan,
  pct,
  type Point,
  seriesBy,
  teamTotals,
  ticks,
} from "../lib/dashboard";
import { fmtNumber } from "../lib/format";
import { customRange, type DayRange, presetRange, PRESETS, rangeDays, shortDay } from "../lib/range";
import type { DashboardRange, DashMember } from "../lib/types";
import { Card, Empty, Stat } from "./ui";

// ------------------------------------------------------------------ the date filter

/** Presets first, then any days a person picks; one row above everything it scopes. */
export function DateFilter(
  { range, today, onChange }: { range: DayRange; today: string; onChange: (r: DayRange) => void },
) {
  const [custom, setCustom] = useState(range.preset === "custom");
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const picked = customRange(from, to, today);
  return (
    <div className="date-filter" role="group" aria-label="Days to show">
      <div className="segmented">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={!custom && range.preset === p.key ? "on" : ""}
            aria-pressed={!custom && range.preset === p.key}
            onClick={() => {
              setCustom(false);
              onChange(presetRange(p.key, today));
            }}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          className={custom ? "on" : ""}
          aria-pressed={custom}
          onClick={() => setCustom(true)}
        >
          Pick days
        </button>
      </div>
      {custom && (
        <form
          className="date-pick"
          onSubmit={(e) => {
            e.preventDefault();
            if (picked) onChange(picked);
          }}
        >
          <label>
            From <input type="date" value={from} max={today} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            To <input type="date" value={to} max={today} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button type="submit" className="primary" disabled={!picked}>Show</button>
          {!picked && from && to && <span className="faint">Up to 93 days, ending today at the latest.</span>}
        </form>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ tooltips

interface TipState {
  x: number;
  y: number;
  content: ReactNode;
}

/** One tooltip per chart: shown at the pointer on hover, beside the mark on keyboard focus. Text only, never HTML. */
function useTip() {
  const box = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<TipState | null>(null);
  const place = (clientX: number, clientY: number, content: ReactNode) => {
    const r = box.current?.getBoundingClientRect();
    if (!r) return;
    setTip({ x: Math.min(clientX - r.left, r.width - 8), y: clientY - r.top, content });
  };
  const on = (content: ReactNode) => ({
    onPointerMove: (e: React.PointerEvent) => place(e.clientX, e.clientY, content),
    onPointerLeave: () => setTip(null),
    onFocus: (e: React.FocusEvent) => {
      const m = e.currentTarget.getBoundingClientRect();
      place(m.left + m.width / 2, m.top, content);
    },
    onBlur: () => setTip(null),
  });
  const view = tip && (
    <div className="viz-tip" role="status" style={{ left: tip.x, top: tip.y }}>
      {tip.content}
    </div>
  );
  return { box, on, view };
}

/** A tooltip's lines: the value strong, the series beside a short key of its colour. */
function TipLines({ title, rows }: { title: string; rows: { key?: "yes" | "no"; value: string; label: string }[] }) {
  return (
    <>
      <div className="viz-tip-title">{title}</div>
      {rows.map((r) => (
        <div className="viz-tip-row" key={r.label}>
          {r.key
            ? <span className={`viz-key line ${r.key}`} aria-hidden />
            : <span className="viz-key none" aria-hidden />}
          <strong>{r.value}</strong> <span>{r.label}</span>
        </div>
      ))}
    </>
  );
}

function Legend({ items }: { items: { key: "yes" | "no"; label: string }[] }) {
  return (
    <div className="viz-legend">
      {items.map((i) => (
        <span key={i.key}>
          <span className={`viz-key ${i.key}`} aria-hidden /> {i.label}
        </span>
      ))}
    </div>
  );
}

/** A chart card's switch between the chart and its table. */
function useTableView(): [boolean, ReactNode] {
  const [table, setTable] = useState(false);
  return [
    table,
    <button type="button" className="link-button" onClick={() => setTable(!table)}>
      {table ? "Show chart" : "Show as table"}
    </button>,
  ];
}

// ------------------------------------------------------------------ the day's numbers

export function RangeStats({ members }: { members: DashMember[] }) {
  const t = teamTotals(members);
  return (
    <div className="grid cols-3">
      <Stat
        label="Calls placed"
        value={fmtNumber(t.dialed)}
        hint={`${fmtNumber(t.dialed_connected)} connected (${pct(t.dialed_connected, t.dialed)})`}
      />
      <Stat
        label="Talk time"
        value={fmtTalk(t.talk_seconds)}
        hint={t.dialed_connected + t.received > 0
          ? `${fmtTalk(t.talk_seconds / (t.dialed_connected + t.received))} a call that connected`
          : "No call connected"}
      />
      <Stat
        label="Customers reached"
        value={fmtNumber(t.customers_reached)}
        hint="Spoken to on a call that connected"
      />
      <Stat
        label="Interested"
        value={fmtNumber(t.interested)}
        hint={`${fmtNumber(t.call_back)} call backs · ${fmtNumber(t.visits_booked)} visits booked`}
      />
      <Stat
        label="Follow-ups done"
        value={fmtNumber(t.follow_ups_done)}
        hint={`${pct(t.follow_ups_on_time, t.follow_ups_done)} on time`}
      />
      <Stat
        label="Missed calls"
        value={fmtNumber(t.missed)}
        alert={t.missed > 0}
        hint={`${fmtNumber(t.received)} calls received and answered`}
      />
    </div>
  );
}

// ------------------------------------------------------------------ the agents side by side

const clock = (iso: string, tz: string) =>
  new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", timeZone: tz }).format(new Date(iso));

/** Each person in a row: calls placed (connected and not answered) and talk time as bars on one scale per column. */
export function AgentCompare({ data, tz, multiDay }: { data: DashboardRange; tz: string; multiDay: boolean }) {
  const { box, on, view } = useTip();
  const agents = agentsToShow(data.members);
  const maxDialed = Math.max(1, ...agents.map((a) => a.dialed));
  const maxTalk = Math.max(1, ...agents.map((a) => a.talk_seconds));
  return (
    <Card title="Agents compared" flush>
      {agents.length === 0 ? <Empty>No telecallers yet.</Empty> : (
        <div className="viz" ref={box}>
          <div className="viz-head">
            <Legend items={[{ key: "yes", label: "Connected" }, { key: "no", label: "Not answered" }]} />
          </div>
          <div className="table-wrap">
            <table className="agent-table">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Calls placed</th>
                  <th className="num">Connected</th>
                  <th>Talk time</th>
                  <th className="num">Reached</th>
                  <th className="num">Interested</th>
                  <th className="num">Follow-ups done</th>
                  {!multiDay && <th>On calls</th>}
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => {
                  const rate = connectRate(a);
                  return (
                    <tr key={a.member_id}>
                      <td className="primary nowrap">{a.name}</td>
                      <td>
                        <div className="bar-cell">
                          <div
                            className="hbar-stack"
                            style={{ width: `${(a.dialed / maxDialed) * 100}%` }}
                            tabIndex={0}
                            aria-label={`${a.name}: ${a.dialed} calls placed, ${a.dialed_connected} connected, ${a.not_answered} not answered`}
                            {...on(
                              <TipLines
                                title={a.name}
                                rows={[
                                  { key: "yes", value: fmtNumber(a.dialed_connected), label: "connected" },
                                  { key: "no", value: fmtNumber(a.not_answered), label: "not answered" },
                                  { value: fmtNumber(a.dialed), label: "calls placed" },
                                ]}
                              />,
                            )}
                          >
                            {a.dialed_connected > 0 && (
                              <span className="yes" style={{ flexGrow: a.dialed_connected }} />
                            )}
                            {a.not_answered > 0 && <span className="no" style={{ flexGrow: a.not_answered }} />}
                          </div>
                          <span className="bar-value">{fmtNumber(a.dialed)}</span>
                        </div>
                      </td>
                      <td className="num">{rate === null ? "–" : `${Math.round(rate * 100)}%`}</td>
                      <td>
                        <div className="bar-cell">
                          <div
                            className="hbar-stack"
                            style={{ width: `${(a.talk_seconds / maxTalk) * 100}%` }}
                            tabIndex={0}
                            aria-label={`${a.name}: ${fmtTalk(a.talk_seconds)} on calls`}
                            {...on(
                              <TipLines
                                title={a.name}
                                rows={[{
                                  key: "yes",
                                  value: fmtTalk(a.talk_seconds),
                                  label: "on calls that connected",
                                }]}
                              />,
                            )}
                          >
                            {a.talk_seconds > 0 && <span className="yes" style={{ flexGrow: 1 }} />}
                          </div>
                          <span className="bar-value">{fmtTalk(a.talk_seconds)}</span>
                        </div>
                      </td>
                      <td className="num">{fmtNumber(a.customers_reached)}</td>
                      <td className="num">{fmtNumber(a.interested)}</td>
                      <td className="num nowrap">
                        {fmtNumber(a.follow_ups_done)}
                        {a.follow_ups_done > 0 && (
                          <span className="faint">· {pct(a.follow_ups_on_time, a.follow_ups_done)} on time</span>
                        )}
                      </td>
                      {!multiDay && (
                        <td className="nowrap">
                          {a.first_call_at && a.last_call_end_at
                            ? `${clock(a.first_call_at, tz)} – ${clock(a.last_call_end_at, tz)}`
                            : <span className="faint">No calls</span>}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {view}
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ everyone through the day

/** A row per person, a cell per hour of the day (or per day): the more calls, the deeper the emerald. */
export function ActivityHeatmap({ data, multiDay }: { data: DashboardRange; multiDay: boolean }) {
  const { box, on, view } = useTip();
  const [table, toggle] = useTableView();
  const [chosen, setBy] = useState<"hour" | "day">("hour");
  const by = multiDay ? chosen : "hour";
  const agents = agentsToShow(data.members);
  const buckets = by === "hour" ? data.hours : data.days;
  const columns: (number | string)[] = by === "hour" ? hourColumns(data.hours) : rangeDays(data);
  const label = (k: number | string) => (typeof k === "number" ? hourLabel(k) : shortDay(k));
  const span = (k: number | string) => (typeof k === "number" ? hourSpan(k) : shortDay(k));
  const cells = agents.map((a) => columns.map((k) => cellOf(buckets, a.member_id, k, by)));
  const max = Math.max(0, ...cells.flat().map((c) => c.calls));
  return (
    <Card
      title={by === "hour" ? "Calls through the day" : "Calls day by day"}
      actions={
        <>
          {multiDay && (
            <div className="segmented small">
              <button type="button" className={by === "hour" ? "on" : ""} onClick={() => setBy("hour")}>By hour</button>
              <button type="button" className={by === "day" ? "on" : ""} onClick={() => setBy("day")}>By day</button>
            </div>
          )}
          {toggle}
        </>
      }
    >
      {agents.length === 0 ? <Empty>No telecallers yet.</Empty> : table
        ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Agent</th>
                  {columns.map((k) => <th className="num" key={k}>{label(k)}</th>)}
                </tr>
              </thead>
              <tbody>
                {agents.map((a, i) => (
                  <tr key={a.member_id}>
                    <td className="nowrap">{a.name}</td>
                    {cells[i].map((c) => <td className="num" key={c.key}>{c.calls || ""}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
        : (
          <div className="viz" ref={box}>
            {multiDay && by === "hour" && (
              <p className="faint viz-note">All {rangeDays(data).length} days together, by the hour they happened.</p>
            )}
            <div
              className="heat"
              style={{ gridTemplateColumns: `minmax(90px, max-content) repeat(${columns.length}, minmax(18px, 1fr))` }}
            >
              <span />
              {columns.map((k, j) => (
                <span className="heat-x" key={k}>{j % 2 === 0 || columns.length <= 8 ? label(k) : ""}</span>
              ))}
              {agents.map((a, i) => (
                <div className="heat-row" key={a.member_id} style={{ display: "contents" }}>
                  <span className="heat-name">{a.name}</span>
                  {cells[i].map((c) => (
                    <span
                      key={c.key}
                      className={`heat-cell s${heatStep(c.calls, max)}`}
                      role="img"
                      aria-label={`${a.name}, ${span(c.key)}: ${c.calls} calls, ${c.connected} connected`}
                      {...on(
                        <TipLines
                          title={`${a.name} · ${span(c.key)}`}
                          rows={[
                            { value: fmtNumber(c.calls), label: c.calls === 1 ? "call" : "calls" },
                            { key: "yes", value: fmtNumber(c.connected), label: "connected" },
                            { value: fmtTalk(c.talk_seconds), label: "talk" },
                          ]}
                        />,
                      )}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div className="heat-scale" aria-hidden>
              <span className="faint">Fewer</span>
              <span className="heat-cell s0" />
              {Array.from({ length: HEAT_STEPS }, (_, i) => <span className={`heat-cell s${i + 1}`} key={i} />)}
              <span className="faint">More calls{max > 0 ? ` (busiest: ${max})` : ""}</span>
            </div>
            {view}
          </div>
        )}
    </Card>
  );
}

// ------------------------------------------------------------------ one person's day

/** One person's day (or the whole team's), half hour by half hour or day by day: connected calls at the base, the rest
 * above. */
export function AgentDay({ data, multiDay, manager }: { data: DashboardRange; multiDay: boolean; manager: boolean }) {
  const { box, on, view } = useTip();
  const [table, toggle] = useTableView();
  const agents = agentsToShow(data.members);
  const [who, setWho] = useState<string>(manager ? "team" : (agents[0]?.member_id ?? "team"));
  const [chosen, setBy] = useState<"half" | "day">("half");
  const by = multiDay ? chosen : "half";
  const memberId = who === "team" ? null : who;
  const columns: (number | string)[] = by === "half" ? halfColumns(data.hours) : rangeDays(data);
  const points = seriesBy(by === "half" ? data.hours : data.days, memberId, columns, by);
  const max = Math.max(0, ...points.map((p) => p.calls));
  const scale = ticks(max);
  const top = scale[scale.length - 1];
  const name = memberId ? (agents.find((a) => a.member_id === memberId)?.name ?? "") : "Whole team";
  const span = (p: Point) => (typeof p.key === "number" ? halfSpan(p.key) : shortDay(p.key));
  // Under the columns: every two hours at the start of the hour (every four on a long day), or every few days under
  // the day's column. Columns are 2px apart.
  const hoursApart = columns.length > 32 ? 4 : 2;
  const every = columns.length > 16 ? 3 : columns.length > 8 ? 2 : 1;
  const xLabel = (p: Point, i: number) =>
    typeof p.key === "number"
      ? (p.key % (hoursApart * 60) === 0 ? hourLabel(p.key / 60) : "")
      : (i % every === 0 ? shortDay(p.key) : "");
  const xAt = (i: number) => `calc(${by === "half" ? i : i + 0.5} * (100% + 2px) / ${points.length} - 1px)`;
  const busiest = points.reduce<Point | null>((b, p) => (p.calls > (b?.calls ?? 0) ? p : b), null);
  return (
    <Card
      title={manager ? "One agent's day" : "Your day"}
      actions={
        <>
          {manager && (
            <select value={who} onChange={(e) => setWho(e.target.value)} aria-label="Whose calls">
              <option value="team">Whole team</option>
              {agents.map((a) => <option key={a.member_id} value={a.member_id}>{a.name}</option>)}
            </select>
          )}
          {multiDay && (
            <div className="segmented small">
              <button type="button" className={by === "half" ? "on" : ""} onClick={() => setBy("half")}>
                By half hour
              </button>
              <button type="button" className={by === "day" ? "on" : ""} onClick={() => setBy("day")}>By day</button>
            </div>
          )}
          {toggle}
        </>
      }
    >
      {table
        ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{by === "half" ? "Time" : "Day"}</th>
                  <th className="num">Calls</th>
                  <th className="num">Connected</th>
                  <th className="num">Talk</th>
                </tr>
              </thead>
              <tbody>
                {points.map((p) => (
                  <tr key={p.key}>
                    <td>{span(p)}</td>
                    <td className="num">{p.calls}</td>
                    <td className="num">{p.connected}</td>
                    <td className="num">{fmtTalk(p.talk_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
        : (
          <div className="viz" ref={box}>
            <div className="viz-head">
              <Legend items={[{ key: "yes", label: "Connected" }, { key: "no", label: "Not connected" }]} />
              {busiest && (
                <span className="faint">
                  {name}: busiest {span(busiest)}, {busiest.calls} {busiest.calls === 1 ? "call" : "calls"}
                </span>
              )}
            </div>
            {multiDay && by === "half" && (
              <p className="faint viz-note">
                All {rangeDays(data).length} days together, by the half hour they happened.
              </p>
            )}
            {max === 0
              ? <Empty>No calls {memberId ? `for ${name} ` : ""}on these days.</Empty>
              : (
                <div className="cols-chart">
                  <div className="cols-plot">
                    {scale.map((t) => (
                      <div className="cols-grid" key={t} style={{ bottom: `${(t / top) * 100}%` }}>
                        <span>{t}</span>
                      </div>
                    ))}
                    <div className="cols">
                      {points.map((p) => (
                        <div className="col-slot" key={p.key}>
                          <div
                            className="col"
                            style={{ height: `${(p.calls / top) * 100}%` }}
                            tabIndex={p.calls > 0 ? 0 : -1}
                            aria-label={`${span(p)}: ${p.calls} calls, ${p.connected} connected`}
                            {...on(
                              <TipLines
                                title={`${name} · ${span(p)}`}
                                rows={[
                                  { key: "yes", value: fmtNumber(p.connected), label: "connected" },
                                  { key: "no", value: fmtNumber(p.calls - p.connected), label: "not connected" },
                                  { value: fmtTalk(p.talk_seconds), label: "talk" },
                                ]}
                              />,
                            )}
                          >
                            {p.calls - p.connected > 0 && (
                              <span className="no" style={{ flexGrow: p.calls - p.connected }} />
                            )}
                            {p.connected > 0 && <span className="yes" style={{ flexGrow: p.connected }} />}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="cols-x" aria-hidden>
                    {points.map((p, i) => {
                      const text = xLabel(p, i);
                      return text ? <span key={p.key} style={{ left: xAt(i) }}>{text}</span> : null;
                    })}
                  </div>
                </div>
              )}
            {view}
          </div>
        )}
    </Card>
  );
}
